/**
 * 功能配额信息
 */
export interface FeatureQuota {
    total: number; // 总配额
    used: number; // 已使用量
    remaining: number; // 剩余量
}

/**
 * 功能权限信息
 */
export interface Feature {
    featureName: string; // 功能名称
    /**
     * 是否可用（不考虑用量限制）
     */
    isAvailable: boolean;
    /**
     * 是否达到用量限制
     */
    isQuotaExceeded: boolean;
}

/**
 * 用户套餐类型
 */
export type PlanType = 'free' | 'pro' | 'teams' | 'enterprise';

/**
 * 用户套餐信息
 */
export interface UserPlan {
    planType: PlanType; // 套餐类型
    planName: string; // 套餐名称
}

/**
 * 功能键值枚举
 */
export type FeatureKey = 'feature_foo'
| 'feature_bar'
| '查新:查看完整检索结果'
| '查新:查看检索式'
| '查新:查看检索结果'
| '查新:添加手动检索'
| '查新:执行查新检索'
| '查新:编辑权利要求树节点'
| '查新:添加权利要求树节点'
| '查新:删除权利要求树节点'
| '查新:添加专利'
| '说明书:生成'
| '说明书:生成:EU'
| '说明书:生成:US'
| '说明书:生成:CN'
| '说明书:上传附件'
| '说明书:重新生成'
| '说明书:添加附图'
| '说明书:编辑附图'
| '说明书:切换审查模式'
| '说明书:段落优化'
| '说明书:段落编辑'
| 'FTO:Create';

/**
 * Feature Init 响应数据
 */
export interface FeatureInitResponse {
    features: Record<FeatureKey, Feature>;
    userPlan: UserPlan;
}

/**
 * 产品准入决策动作类型
 *
 * 由后端 `/account-privilege/product-onboarding/check` 接口返回，
 * 替代旧版 `need-add-trial` 接口的 boolean 返回值，用于表达更丰富的准入决策分支。
 *
 * @remarks 字段值与后端保持 snake_case 一致，便于直接映射接口返回。
 */
export enum NeedAddTrialActionType {
    /** 当前用户已有权限，直接进入产品，前端无需任何操作 */
    DIRECT_LOGIN = 'direct_login',
    /** 个人租户已存在且有目标产品权限，引导用户切换到个人租户 */
    SWITCH_TO_INDIVIDUAL_TENANT = 'switch_to_individual_tenant',
    /** 个人租户已存在但没有目标产品权限，引导切换后再完成赋权流程 */
    SWITCH_AND_GRANT_PRIVILEGE = 'switch_and_grant_privilege',
    /** 后端已在本次 check 请求中完成赋权，前端清缓存并刷新页面使权限生效 */
    GRANT_INDIVIDUAL_TENANT_PRIVILEGE = 'grant_individual_tenant_privilege',
    /** 企业允许创建个人租户但租户不存在，引导用户创建/切换个人租户 */
    CREATE_INDIVIDUAL_TENANT = 'create_individual_tenant',
    /** 企业不允许创建个人租户，提示联系管理员 */
    CONTACT_ADMIN = 'contact_admin',
}

/**
 * 产品准入检查接口响应体
 *
 * 对应后端 `/account-privilege/product-onboarding/check` 接口的 data 结构。
 */
export interface ProductOnboardingCheckResponse {
    /** 准入决策动作类型 */
    action_type: NeedAddTrialActionType | string;
}

/**
 * 添加试用扩展参数体
 */
export interface AddTrialExtendBody {
    /**
     * 账号注册来源
     */
    channel_source?: string;
    /**
     * 活动类型
     */
    activity_type?: string;
    /**
     * 邀请人id
     */
    inviter_id?: string;
    /**
     * 企业邀请code 企业邀请注册时有值
     */
    invitation_code?: string;
    /**
     * 设备类型
     */
    media_type?: 'Mobile' | '';
    /**
     * 邀请注册来源
     */
    from?: string;
    /**
     * 其他参数
     */
    [key: string]: any;
}

export interface ShareState {
    isShareModel: boolean;
}

/**
 * 用户安全控制配置
 * 字段保持后端 snake_case 命名，方便直接映射接口返回。
 */
export interface UserSecurityControl {
    company_id?: string;
    incognito_flag?: boolean;
    pdf_download_and_export_flag?: boolean;
    create_customized_field_flag?: boolean;
    comment_patent_flag?: boolean;
    upload_flag?: boolean;
    display_free_product_flag?: boolean;
    ip_upload_disabled?: boolean;
    [key: string]: unknown;
}
